import React, { useMemo } from 'react';
import { createStyles, makeStyles, Grid } from '@material-ui/core';
import { AlertBox, Footer } from 'neon-design-system';
import { ViewTrackerProvider } from '../../providers/ViewTrackerProvider';
import { useUser, TrialState } from '../../providers/UserProvider';
import { Container } from '../../ui/Container'
import { config } from '../../../config';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { formatTimeRemaining } from '../admin/UserList';

interface ClientContainerProps {
	children: React.ReactNode;
	withHeader?: boolean;
}

const useStyles = makeStyles(theme => createStyles({
	root: {
		position: 'absolute',
		display: 'flex',
		bottom: 0,
		left: 0,
		right: 0,
		top: 48,
		flexDirection: 'column',
		'&>div>div': {
			minWidth: 0
		},
		'@media print': {
			top: 0
		}
	},
	dataContainer: {
		display: 'flex',
		height: '100%',
		'& > div > div:last-child': {
			minWidth: 0
		}
	},
	containerWithoutTheHeader: {
		flexGrow: 1,
		marginBottom: theme.spacing(7),
		minWidth: 0
	},
	container: {
		flexGrow: 1,
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(7),
		minWidth: 0,
		[theme.breakpoints.up('lg')]: {
			marginTop: theme.spacing(4)
		}
	},
	footerGridItem: {
		alignSelf: 'flex-end'
	},
	footer: {
		marginTop: 0,
		'@media print': {
			display: 'none'
		}
	}
}));

const InsightsFooter = () => {
	const classes = useStyles();
	return (
		<Footer
			className={classes.footer}
		/>
	);
};

const ClientContainer = ({ children, withHeader = true }: ClientContainerProps) => {
	const classes = useStyles();
	const { trialState } = useUser();
	const { data: userData } = useQuery(gql`
		query me {
			me {
				trialMinLeft
			}
		}
	`, { fetchPolicy: 'network-only' });
	const daysRemaining = (remainDays: string) => `you have ${remainDays} free access to select articles`;
	const expiryMessage = useMemo(() => {
		return userData?.me.trialMinLeft
			? userData.me.trialMinLeft < 24 * 60 // 1 day
				? 'your trial expires today'
				: daysRemaining(formatTimeRemaining(userData.me.trialMinLeft, true))
			: daysRemaining(`${config.trialDays} days`)
	}, [userData]);

	return (
		<ViewTrackerProvider>
			<div className={classes.root}>
				{(trialState === TrialState.TrialContinues) &&
					<div>
						<AlertBox
							id="trial-user-alert-box"
							visible={true}
							children={`Welcome to Neon Insights, ${expiryMessage}. A member of Marex will contact you to complete your registration.`}
							styleName="info" /></div>
				}
				<div className={classes.dataContainer}>
					<Grid container>
						<Container>
							<div className={withHeader ? classes.container : classes.containerWithoutTheHeader}>
								{children}
							</div>
						</Container>
						<Grid item xs={12} className={classes.footerGridItem}>
							<InsightsFooter />
						</Grid>
					</Grid>
				</div>
			</div>
		</ViewTrackerProvider>
	);
};

export default ClientContainer;
